import React, { useState } from "react";
import {
  WaitingManager,
  ConfirmManager,
  ToastManager,
  SlideManager,
} from "components/organisms/CommonUI";
import Button from "components/atoms/Button";
import styled from "styled-components";
import KIDS_GERI from "assets/docs/KIDS_GERI";
import SPSTv3 from "assets/docs/SPSTv3";
import POLY from "assets/docs/POLY";
import CCMM from "assets/docs/CCMM";
import AIReviewDetailTemplate from "components/templates/AIReviewDetailTemplate";
import { AIReview, AIReviewStatus, PromptType } from "common/models/types";

const TestContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const CodeBox = styled.pre`
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow: auto;
  margin-bottom: 24px;
  font-size: 14px;
`;

const ReferenceViewer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  max-height: 80vh;
  max-width: 600px;
  margin-right: auto;
  overflow-y: auto;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: monospace;
  resize: vertical;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ReportViewerContainer = styled.div`
  margin-top: 24px;
  padding: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
`;

const TestPage: React.FC = () => {
  const [selectedReference, setSelectedReference] = useState<string | null>(null);
  const [aiReportInput, setAiReportInput] = useState<string>("");
  const [mockReview, setMockReview] = useState<AIReview | null>(null);

  // WaitingPopup 테스트 함수
  const showBasicWaiting = () => {
    WaitingManager.show({ message: "기본 로딩 중..." });
    // 3초 후 자동으로 닫힘
    setTimeout(() => {
      WaitingManager.hide();
    }, 3000);
  };

  const showProgressWaiting = () => {
    WaitingManager.show({
      message: "진행 중...",
      showProgress: true,
      progress: 0,
    });

    // 진행률 시뮬레이션
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      WaitingManager.updateProgress(progress);

      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          WaitingManager.hide();
        }, 500);
      }
    }, 500);
  };

  const updateWaitingMessage = () => {
    WaitingManager.show({ message: "초기 메시지" });

    setTimeout(() => {
      WaitingManager.updateMessage("업데이트된 메시지");
    }, 1500);

    setTimeout(() => {
      WaitingManager.hide();
    }, 3000);
  };

  // ConfirmPopup 테스트 함수
  const showBasicConfirm = () => {
    ConfirmManager.show({
      title: "기본 확인",
      body: "계속 진행하시겠습니까?",
      buttons: [
        <Button
          key="cancel"
          children="취소"
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          style={{ width: "100%" }}
          onClick={() => ConfirmManager.hide()}
        />,
        <Button
          key="confirm"
          children="확인"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          style={{ width: "100%" }}
          onClick={() => ConfirmManager.hide()}
        />,
      ],
    });
  };

  const showCustomConfirm = () => {
    ConfirmManager.show({
      title: "데이터 삭제",
      body: (
        <div>
          삭제된 데이터는 <strong>복구할 수 없습니다</strong>. 계속하시겠습니까?
        </div>
      ),
      buttons: [
        <Button
          key="cancel"
          children="취소"
          type="Text"
          size="Medium"
          variant="Ghost"
          color="Secondary"
          style={{ width: "100%" }}
          onClick={() => ConfirmManager.hide()}
        />,
        <Button
          key="delete"
          children="삭제"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Negative"
          style={{ width: "100%" }}
          onClick={() => {
            ToastManager.show({ content: "데이터가 삭제되었습니다" });
            ConfirmManager.hide();
          }}
        />,
      ],
      showBackdrop: true,
      backdropClose: true,
      showCloseButton: true,
    });
  };

  // ToastPopup 테스트 함수
  const showBasicToast = () => {
    ToastManager.show({ content: "기본 토스트 메시지입니다" });
  };

  const showPositionedToast = (position: "top" | "bottom" | "top-right" | "bottom-right") => {
    ToastManager.show({
      content: `${position} 위치에 표시되는 토스트입니다`,
      position,
      duration: 3000,
    });
  };

  // SlidePopup 테스트 함수
  const showBasicSlide = () => {
    SlideManager.show({
      body: <div>기본 슬라이드 패널입니다.</div>,
      direction: "right",
    });
  };

  const showCustomSlide = (direction: "right" | "left" | "top" | "bottom") => {
    SlideManager.show({
      body: (
        <div style={{ padding: "20px" }}>
          <h3>커스텀 슬라이드 패널</h3>
          <p>다양한 방향에서 슬라이드되는 패널을 사용할 수 있습니다.</p>
          <p>
            현재 방향: <strong>{direction}</strong>
          </p>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => SlideManager.hide()}
            style={{ marginTop: "20px" }}
          >
            닫기
          </Button>
        </div>
      ),
      direction: direction,
      width: direction === "left" || direction === "right" ? "400px" : undefined,
      height: direction === "top" || direction === "bottom" ? "300px" : undefined,
      needCloseButton: true,
    });
  };

  // AI 보고서 변환 함수
  const handleConvertReport = () => {
    if (!aiReportInput.trim()) {
      ToastManager.show({ content: "AI 검토 결과를 입력해주세요." });
      return;
    }

    try {
      // Mock AIReview 객체 생성
      const mockAIReview: AIReview = {
        id: 999,
        patientId: 1,
        promptType: PromptType.MEDICATION_COUNSELING,
        status: AIReviewStatus.COMPLETED,
        reviewResult: aiReportInput,
        createdAt: new Date(),
        updatedAt: new Date(),
        reviewDate: new Date(),
        pinnedAt: undefined,
        memo: undefined,
      };

      setMockReview(mockAIReview);
      ToastManager.show({ content: "보고서 변환이 완료되었습니다." });
    } catch (error) {
      ToastManager.show({ content: "보고서 변환 중 오류가 발생했습니다." });
      console.error("Report parsing error:", error);
    }
  };

  const handleClearReport = () => {
    setAiReportInput("");
    setMockReview(null);
    ToastManager.show({ content: "입력이 초기화되었습니다." });
  };

  // 인쇄 버튼 핸들러
  const handlePrintReport = () => {
    if (!mockReview) {
      ToastManager.show({ content: "먼저 보고서를 변환해주세요." });
      return;
    }

    // SessionStorage에 리뷰 데이터 저장
    sessionStorage.setItem("print_review_latest", JSON.stringify(mockReview));

    // 새 창으로 인쇄 페이지 열기
    const printWindow = window.open(
      `/ai-reviews/${mockReview.id}/print`,
      "print_preview",
      "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no"
    );

    if (!printWindow) {
      ToastManager.show({ content: "팝업 차단을 해제하고 다시 시도해주세요." });
    }
  };

  return (
    <TestContainer>
      <Title>공통 UI 컴포넌트 테스트</Title>

      <Section>
        <SectionTitle>1. WaitingPopup</SectionTitle>
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showBasicWaiting}
          >
            기본 로딩
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showProgressWaiting}
          >
            진행률 표시
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={updateWaitingMessage}
          >
            메시지 업데이트
          </Button>
        </ButtonGroup>
        <CodeBox>{`// 기본 사용법
WaitingManager.show({ message: "로딩 중..." });

// 진행률 표시
WaitingManager.show({ 
  message: "진행 중...", 
  showProgress: true, 
  progress: 0 
});
WaitingManager.updateProgress(50);

// 메시지 업데이트
WaitingManager.updateMessage("새 메시지");

// 숨기기
WaitingManager.hide();`}</CodeBox>
      </Section>

      <Section>
        <SectionTitle>2. ConfirmPopup</SectionTitle>
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showBasicConfirm}
          >
            기본 확인
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showCustomConfirm}
          >
            커스텀 확인
          </Button>
        </ButtonGroup>
        <CodeBox>{`// 기본 사용법
ConfirmManager.show({
  title: "확인",
  body: "계속 진행하시겠습니까?",
  buttons: [
    <Button
      key="cancel"
      children="취소"
      type="Text"
      size="Medium"
      variant="Ghost"
      color="Secondary"
      style={{ width: "100%" }}
      onClick={() => ConfirmManager.hide()}
    />,
    <Button
      key="confirm"
      children="확인"
      type="Text"
      size="Medium"
      variant="Contained"
      color="Primary"
      style={{ width: "100%" }}
      onClick={() => ConfirmManager.hide()}
    />
  ]
});

// 고급 설정
ConfirmManager.show({
  title: "데이터 삭제",
  body: <div>삭제된 데이터는 <strong>복구할 수 없습니다</strong>...</div>,
  buttons: [
    <Button
      key="cancel"
      children="취소"
      type="Text"
      size="Medium"
      variant="Ghost"
      color="Secondary"
      style={{ width: "100%" }}
      onClick={() => ConfirmManager.hide()}
    />,
    <Button
      key="delete"
      children="삭제"
      type="Text"
      size="Medium"
      variant="Contained"
      color="Negative"
      style={{ width: "100%" }}
      onClick={handleDelete}
    />
  ],
  showBackdrop: true,       // 배경 어둡게 처리
  backdropClose: true,      // 배경 클릭 시 닫기 가능
  showCloseButton: true     // X 버튼 표시
});`}</CodeBox>
      </Section>

      <Section>
        <SectionTitle>3. ToastPopup</SectionTitle>
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showBasicToast}
          >
            기본 토스트
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showPositionedToast("top")}
          >
            상단 토스트
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showPositionedToast("bottom")}
          >
            하단 토스트
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showPositionedToast("top-right")}
          >
            우상단 토스트
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showPositionedToast("bottom-right")}
          >
            우하단 토스트
          </Button>
        </ButtonGroup>
        <CodeBox>{`// 기본 사용법 (상단 중앙, 2초)
ToastManager.show({ content: "메시지입니다" });

// 위치 및 표시 시간 지정
ToastManager.show({ 
  content: "우하단 메시지", 
  position: "bottom-right", 
  duration: 5000  // 5초 동안 표시
});

// 컴포넌트 포함
ToastManager.show({ 
  content: (
    <div className="toast-content">
      <Icon name="check-circle" />
      <span>작업이 완료되었습니다</span>
    </div>
  )
});`}</CodeBox>
      </Section>

      <Section>
        <SectionTitle>4. SlidePopup</SectionTitle>
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={showBasicSlide}
          >
            기본 슬라이드
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showCustomSlide("right")}
          >
            우측 슬라이드
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showCustomSlide("left")}
          >
            좌측 슬라이드
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showCustomSlide("top")}
          >
            상단 슬라이드
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => showCustomSlide("bottom")}
          >
            하단 슬라이드
          </Button>
        </ButtonGroup>
        <CodeBox>{`// 기본 사용법
SlideManager.show({
  body: <div>기본 슬라이드 패널입니다.</div>,
  direction: "right",
});

// 고급 설정
SlideManager.show({
  body: (
    <div style={{ padding: "20px" }}>
      <h3>커스텀 슬라이드 패널</h3>
      <p>다양한 방향에서 슬라이드되는 패널을 사용할 수 있습니다.</p>
      <p>현재 방향: <strong>{direction}</strong></p>
      <Button
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        onClick={() => SlideManager.hide()}
        style={{ marginTop: "20px" }}
      >
        닫기
      </Button>
    </div>
  ),
  direction: direction,
  width: direction === "left" || direction === "right" ? "400px" : undefined,
  height: direction === "top" || direction === "bottom" ? "300px" : undefined,
  needCloseButton: true,
});`}</CodeBox>
      </Section>

      <Section>
        <SectionTitle>5. AI 검토 결과 → 보고서 변환기</SectionTitle>
        <p style={{ marginBottom: "16px", color: "#666" }}>
          AI에서 받은 검토 결과를 붙여넣으면 보고서 형식으로 변환하여 표시합니다.
        </p>
        <TextArea
          placeholder="AI 검토 결과를 여기에 붙여넣으세요..."
          value={aiReportInput}
          onChange={(e) => setAiReportInput(e.target.value)}
        />
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={handleConvertReport}
          >
            변환하기
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Outlined"
            color="Secondary"
            onClick={handleClearReport}
          >
            초기화
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant="Contained"
            color="Secondary"
            onClick={handlePrintReport}
            disabled={!mockReview}
          >
            인쇄하기
          </Button>
        </ButtonGroup>

        {mockReview && (
          <ReportViewerContainer>
            <AIReviewDetailTemplate
              review={mockReview}
              onAdminDetail={undefined}
              onTogglePin={() => {}}
              onEditMemo={() => {}}
              onDeleteReport={() => {}}
              onReviewUpdate={() => {}}
              isFullscreen={false}
              onToggleFullscreen={() => {}}
            />
          </ReportViewerContainer>
        )}

        <CodeBox>{`// AI 보고서 변환기 사용법
import AIReviewDetailTemplate from "components/templates/AIReviewDetailTemplate";
import { AIReview, PromptType, AIReviewStatus } from "common/models/types";

// AIReview 객체를 만들어서 표시
const review: AIReview = {
  id: 999,
  patientId: 1,
  promptType: PromptType.MEDICATION_COUNSELING,
  status: AIReviewStatus.COMPLETED,
  reviewResult: "여기에 마크다운 내용",
  createdAt: new Date(),
  updatedAt: new Date(),
  reviewDate: new Date(),
};

// 기존 보고서 템플릿 사용
<AIReviewDetailTemplate review={review} />`}</CodeBox>
      </Section>

      <Section>
        <SectionTitle>6. 참고문헌 뷰어 (References)</SectionTitle>
        <ButtonGroup>
          <Button
            type="Text"
            size="Medium"
            variant={selectedReference === "KIDS_GERI" ? "Contained" : "Outlined"}
            color="Primary"
            onClick={() => setSelectedReference(selectedReference === "KIDS_GERI" ? null : "KIDS_GERI")}
          >
            KIDS_GERI (노인 의약품 정보집)
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant={selectedReference === "SPSTv3" ? "Contained" : "Outlined"}
            color="Primary"
            onClick={() => setSelectedReference(selectedReference === "SPSTv3" ? null : "SPSTv3")}
          >
            STOPP/START v3
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant={selectedReference === "POLY" ? "Contained" : "Outlined"}
            color="Primary"
            onClick={() => setSelectedReference(selectedReference === "POLY" ? null : "POLY")}
          >
            POLY (다약제)
          </Button>
          <Button
            type="Text"
            size="Medium"
            variant={selectedReference === "CCMM" ? "Contained" : "Outlined"}
            color="Primary"
            onClick={() => setSelectedReference(selectedReference === "CCMM" ? null : "CCMM")}
          >
            CCMM
          </Button>
        </ButtonGroup>
        {selectedReference && (
          <ReferenceViewer>
            {selectedReference === "KIDS_GERI" && <KIDS_GERI />}
            {selectedReference === "SPSTv3" && <SPSTv3 />}
            {selectedReference === "POLY" && <POLY />}
            {selectedReference === "CCMM" && <CCMM />}
          </ReferenceViewer>
        )}
        <CodeBox>{`// 참고문헌 컴포넌트 사용법
import KIDS_GERI from "assets/docs/KIDS_GERI";
import SPSTv3 from "assets/docs/SPSTv3";

// 컴포넌트로 렌더링
<KIDS_GERI />
<SPSTv3 />

// ID를 통한 섹션 접근 (AI 프롬프트에서 사용)
#KDGERI_COVER        // 표지
#KDGERI_INTRO        // 서론
#KDGERI_CHAR_PK      // 약동학적 특성
#KDGERI_CHAR_PD      // 약력학적 특성
#KDGERI_D015         // 약물 카드 (예: Ibuprofen)`}</CodeBox>
      </Section>
    </TestContainer>
  );
};

export default TestPage;
